import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Shield, Phone, ChevronRight, Check, Bird, RotateCcw, Star, Clock, Bug } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import { Logo } from "../components/Layout";
import { Header } from "../components/Layout";

const API_URL = process.env.REACT_APP_BACKEND_URL;

function ReferralBirdCalculator({ referrerName }) {
  const [panels, setPanels] = useState("");
  const [storeys, setStoreys] = useState("single");
  const [pigeonDuration, setPigeonDuration] = useState("<6m");
  const [addGutterCleaning, setAddGutterCleaning] = useState(false);
  const [addPanelWash, setAddPanelWash] = useState(false);
  const [addPressureWash, setAddPressureWash] = useState(false);
  const [addBirdMite, setAddBirdMite] = useState(false);
  const [dontKnowPanels, setDontKnowPanels] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const panelsRef = useRef(null);

  useEffect(() => {
    const panelCount = parseInt(panels) || 0;
    if (panelCount < 1) { setEstimate(null); return; }
    const tier1Rate = 42, tier2Rate = 45.50, tier3Rate = 49;
    const nestingAdd = pigeonDuration === ">6m" ? 2 : 0;
    let baseTotal;
    if (panelCount <= 16) baseTotal = panelCount * (tier1Rate + nestingAdd);
    else if (panelCount <= 26) baseTotal = (16 * (tier1Rate + nestingAdd)) + ((panelCount - 16) * (tier2Rate + nestingAdd));
    else baseTotal = (16 * (tier1Rate + nestingAdd)) + (10 * (tier2Rate + nestingAdd)) + ((panelCount - 26) * (tier3Rate + nestingAdd));
    let storeyMultiplier = 1, storeyAddition = 0;
    if (storeys === "two") storeyMultiplier = 1.25;
    else if (storeys === "three") { storeyMultiplier = 1.4; storeyAddition = 400; }
    const meshTotal = Math.max(550, Math.round((baseTotal * storeyMultiplier) + storeyAddition));
    const storeyCleanMult = (storeys === "three" ? 1.40 : storeys === "two" ? 1.20 : 1);
    const storeyFlat = (storeys === "three" ? 80 : storeys === "two" ? 50 : 0);
    const panelWashLevy = (storeys === "three" ? 80 : storeys === "two" ? 50 : 0);
    const gutterCleaningPrice = Math.round((60 + panelCount * 8) * storeyCleanMult + storeyFlat);
    const panelWashPriceCalc = Math.round((25 + panelCount * 5) * storeyCleanMult + storeyFlat + panelWashLevy);
    const pressureWashPriceCalc = Math.round((50 + panelCount * 9) * storeyCleanMult + storeyFlat);
    const selectedCleaningCount = [addGutterCleaning, addPanelWash, addPressureWash].filter(Boolean).length;
    const isCleaningBundle = selectedCleaningCount >= 2;
    const rawCleaningTotal = (addGutterCleaning ? gutterCleaningPrice : 0) + (addPanelWash ? panelWashPriceCalc : 0) + (addPressureWash ? pressureWashPriceCalc : 0);
    const cleaningBundleDiscount = isCleaningBundle ? Math.round(rawCleaningTotal * 0.20) : 0;
    const cleaningTotal = rawCleaningTotal - cleaningBundleDiscount;
    const total = meshTotal + cleaningTotal + (addBirdMite ? 200 : 0);
    const launchDiscount = Math.round(total * 0.10);
    setEstimate({ panels: panelCount, meshTotal, gutterCleaningPrice, panelWashPrice: panelWashPriceCalc, pressureWashPrice: pressureWashPriceCalc, selectedCleaningCount, isCleaningBundle, cleaningBundleDiscount, cleaningTotal, birdMitePrice: addBirdMite ? 200 : 0, total, launchDiscount, discountedTotal: total - launchDiscount });
  }, [panels, storeys, pigeonDuration, addGutterCleaning, addPanelWash, addPressureWash, addBirdMite]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!estimate && !dontKnowPanels) return;
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/solar-estimate`, {
        ...formData, panels: estimate?.panels || 0, storeys, pigeonDuration,
        meshTotal: estimate?.meshTotal || 0, gutterCleaning: addGutterCleaning,
        gutterCleaningPrice: estimate?.gutterCleaningPrice || 0, panelWash: addPanelWash,
        panelWashPrice: estimate?.panelWashPrice || 0, pressureWash: addPressureWash,
        pressureWashPrice: estimate?.pressureWashPrice || 0, cleaningBundleDiscount: estimate?.cleaningBundleDiscount || 0,
        birdMiteAddon: addBirdMite, birdMitePrice: estimate?.birdMitePrice || 0,
        estimatedTotal: estimate?.total || 0, dontKnowPanels,
        source: `referral_${referrerName || "unknown"}`
      });
      setSubmitted(true);
      toast.success("Quote sent! We'll be in touch shortly.");
    } catch { toast.error("Failed to send. Please call us."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setPanels(""); setStoreys("single"); setPigeonDuration("<6m"); setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); setAddBirdMite(false); setDontKnowPanels(false);
    setFormData({ name: "", phone: "", email: "", address: "" }); setSubmitted(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="refer-bird-calculator">
      <div className="bg-[#5BC0DE] text-white p-5 flex items-center gap-3">
        <Bird className="w-6 h-6" />
        <h3 className="text-xl font-bold">Solar Panel Bird Proofing</h3>
      </div>
      <div className="grid md:grid-cols-2">
        <div className="p-6 md:p-8 space-y-5">
          <div>
            <Label className="font-medium mb-2 block text-base">Number of solar panels</Label>
            <Input ref={panelsRef} type="number" min="1" value={panels} onChange={(e) => { setPanels(e.target.value); setDontKnowPanels(false); }} placeholder="e.g., 20" className="text-lg py-5" disabled={dontKnowPanels} />
            <button type="button" onClick={() => { setDontKnowPanels(!dontKnowPanels); if (!dontKnowPanels) setPanels(""); }} className={`mt-2 text-sm font-medium ${dontKnowPanels ? 'text-[#7ABC32]' : 'text-[#5BC0DE]'}`}>
              {dontKnowPanels ? "I know my panel count" : "I don't know my panel count"}
            </button>
          </div>
          <div>
            <Label className="font-medium mb-2 block text-base">House height</Label>
            <div className="grid grid-cols-3 gap-3">
              {[{ value: "single", label: "1 Storey" }, { value: "two", label: "2 Storey" }, { value: "three", label: "3+" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setStoreys(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${storeys === opt.value ? 'border-[#5BC0DE] bg-[#5BC0DE]/10 text-[#5BC0DE]' : 'border-slate-200 hover:border-slate-300'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-medium mb-2 block text-base">How long have birds been nesting?</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "<6m", label: "<6 months" }, { value: ">6m", label: ">6 months" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => { setPigeonDuration(opt.value); if (opt.value === "<6m") { setAddGutterCleaning(false); setAddPanelWash(false); setAddPressureWash(false); setAddBirdMite(false); } }}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${pigeonDuration === opt.value ? 'border-[#5BC0DE] bg-[#5BC0DE]/10 text-[#5BC0DE]' : 'border-slate-200 hover:border-slate-300'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          {pigeonDuration === ">6m" && (
            <div className="space-y-2">
              <p className="text-xs font-semibold text-[#7ABC32] uppercase tracking-wide">Cleaning Services {estimate?.isCleaningBundle && <span className="ml-1 bg-[#7ABC32] text-white px-1.5 py-0.5 rounded-full normal-case text-[10px]">Bundle — save 20%!</span>}</p>
              {estimate?.selectedCleaningCount > 0 && estimate?.selectedCleaningCount < 2 && (
                <p className="text-xs text-amber-600 font-medium">Select 2+ for a 20% bundle discount</p>
              )}
              <div className="space-y-2">
                {[
                  { key: "gutter", active: addGutterCleaning, toggle: setAddGutterCleaning, label: "Gutter Clean", price: estimate?.gutterCleaningPrice, desc: "Full gutter flush", details: ["Remove all debris from gutters", "Flush & clear downpipes", "Dispose of waste material"] },
                  { key: "panel", active: addPanelWash, toggle: setAddPanelWash, label: "Panel Wash", price: estimate?.panelWashPrice, desc: "Surface wash panels", details: ["Water-fed broom clean on panel tops", "Remove bird droppings & residue", "Restore panel efficiency"] },
                  { key: "pressure", active: addPressureWash, toggle: setAddPressureWash, label: "Pressure Wash", price: estimate?.pressureWashPrice, desc: "Wash & sanitise", details: ["High-pressure wash around panels", "Sanitise & deodorise affected areas", "Remove staining from roof surface"] },
                ].map(opt => (
                  <label key={opt.key} className={`block p-2.5 rounded-lg cursor-pointer border-2 transition-colors ${opt.active ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-slate-50 border-slate-200 hover:border-slate-300'}`}>
                    <div className="flex items-center gap-3">
                      <Checkbox checked={opt.active} onCheckedChange={opt.toggle} />
                      <span className="font-medium text-sm whitespace-nowrap">{opt.label}</span>
                      <span className="text-xs text-slate-500 hidden sm:inline">— {opt.desc}</span>
                      {opt.price && <span className="text-[#7ABC32] font-bold text-xs ml-auto">${opt.price}</span>}
                    </div>
                    {opt.active && (
                      <ul className="mt-2 ml-8 space-y-0.5">
                        {opt.details.map((d, i) => <li key={i} className="flex items-center gap-1.5 text-xs text-slate-600"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />{d}</li>)}
                      </ul>
                    )}
                  </label>
                ))}
              </div>
            </div>
          )}
          <label className={`flex items-start gap-2 p-3 rounded-lg cursor-pointer border-2 ${addBirdMite ? 'bg-[#7ABC32]/10 border-[#7ABC32]' : 'bg-[#E74C3C]/5 border-[#E74C3C]/30'}`}>
            <Checkbox checked={addBirdMite} onCheckedChange={setAddBirdMite} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Bird Mite Treatment (+$200)</span>
              {addBirdMite && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Specialist bird mite chemical treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Free follow-up treatment if needed</li>
                </ul>
              )}
            </div>
          </label>
        </div>
        <div className="bg-slate-50 p-6 md:p-8 border-t md:border-t-0 md:border-l">
          {!submitted ? (
            <>
              <div className={`rounded-xl overflow-hidden mb-5 ${(estimate || dontKnowPanels) ? 'bg-[#5BC0DE]' : 'bg-slate-300'}`}>
                <div className={`px-4 py-2.5 flex items-center justify-center ${(estimate || dontKnowPanels) ? 'bg-[#3498aa]' : 'bg-slate-400'}`}>
                  <img src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png" alt="Pest Free" className="h-10" />
                </div>
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #7ABC32, #5BC0DE)' }} />
                <div className="p-4 text-white text-center">
                  {dontKnowPanels ? (
                    <div><p className="text-lg font-bold mb-1">No worries!</p><p className="text-sm text-white/80">Tell us your details below and we'll call with a free quote</p></div>
                  ) : estimate ? (
                    <>
                      <div className="flex items-baseline justify-center gap-2">
                        <span className="text-lg text-white/50 line-through">${estimate.total}</span>
                        <span className="text-3xl font-bold">${estimate.discountedTotal}</span>
                      </div>
                      <p className="text-amber-400 font-semibold text-sm">Save ${estimate.launchDiscount} (10% off)</p>
                      <p className="text-xs text-yellow-300 font-semibold mt-1 flex items-center justify-center gap-1"><Clock className="w-3 h-3" /> Offer ends March 31</p>
                    </>
                  ) : <button type="button" onClick={() => { panelsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); panelsRef.current?.focus(); }} className="text-2xl font-bold py-2 cursor-pointer hover:text-white/80 transition-colors">Enter panels</button>}
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Friend's Name *" className="bg-white py-5" />
                  <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Their Phone *" className="bg-white py-5" />
                </div>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Their Email *" className="bg-white py-5" />
                <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Their Address *" rows={2} className="bg-white" />
                <Button type="submit" disabled={(!estimate && !dontKnowPanels) || submitting} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 text-base font-semibold">
                  {submitting ? "Sending..." : "Send Quote to Friend"} <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-3"><Check className="w-6 h-6 text-white" /></div>
              <h4 className="font-bold mb-2">Quote Sent!</h4>
              <p className="text-slate-600 text-sm mb-4">Your friend will receive a professional quote. You'll earn 10% when they book!</p>
              <Button onClick={reset} variant="outline" size="sm"><RotateCcw className="w-4 h-4 mr-1" /> Refer Another Friend</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ReferralPestCalculator({ referrerName }) {
  const [propertyType, setPropertyType] = useState("house");
  const [bedrooms, setBedrooms] = useState("3");
  const [storeys, setStoreys] = useState("single");
  const [treatmentType, setTreatmentType] = useState("standard");
  const [rodentTreatment, setRodentTreatment] = useState(false);
  const [termiteAddOn, setTermiteAddOn] = useState(false);
  const [formData, setFormData] = useState({ name: "", phone: "", email: "", address: "" });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const basePrices = {
    house: { standard: { "1": 165, "2": 175, "3": 198, "4": 220, "5": 250, "6": 285 }, comprehensive: { "1": 220, "2": 240, "3": 280, "4": 330, "5": 380, "6": 450 } },
    unit: { standard: { "1": 150, "2": 170, "3": 190, "4": 210, "5": 240, "6": 270 }, comprehensive: { "1": 195, "2": 220, "3": 250, "4": 290, "5": 330, "6": 380 } }
  };

  const basePrice = basePrices[propertyType]?.[treatmentType]?.[bedrooms] || 198;
  let adjustedPrice = basePrice;
  if (storeys === "double") adjustedPrice += 30;

  let rodentPrice = 0;
  if (rodentTreatment) {
    rodentPrice = treatmentType === "comprehensive" ? 120 : 85;
    if (storeys === "double") rodentPrice += 20;
  }

  let termiteInspection = 0;
  if (termiteAddOn) {
    termiteInspection = 150;
    const bedroomNum = parseInt(bedrooms);
    if (bedroomNum >= 5) termiteInspection += 70;
    else if (bedroomNum >= 4) termiteInspection += 40;
    else if (bedroomNum >= 3) termiteInspection += 20;
    if (storeys === "double") termiteInspection += 60;
  }

  const totalPrice = adjustedPrice + rodentPrice + termiteInspection;
  const launchDiscount = Math.round(totalPrice * 0.10);
  const discountedPrice = totalPrice - launchDiscount;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/pest-estimate`, {
        ...formData, propertyType, bedrooms, storeys, treatmentType,
        serviceFrequency: "oneoff", rodentTreatment, rodentPrice,
        termiteAddOn, termiteInspectionEstimate: termiteInspection || null,
        pricePerVisit: adjustedPrice, source: `referral_${referrerName || "unknown"}`
      });
      setSubmitted(true);
      toast.success("Quote sent! We'll be in touch shortly.");
    } catch { toast.error("Failed to send. Please call us."); }
    finally { setSubmitting(false); }
  };

  const reset = () => {
    setPropertyType("house"); setBedrooms("3"); setStoreys("single");
    setTreatmentType("standard"); setRodentTreatment(false); setTermiteAddOn(false);
    setFormData({ name: "", phone: "", email: "", address: "" }); setSubmitted(false);
  };

  return (
    <div className="bg-white rounded-2xl shadow-xl overflow-hidden" data-testid="refer-pest-calculator">
      <div className="bg-[#E74C3C] text-white p-5 flex items-center gap-3">
        <Bug className="w-6 h-6" />
        <h3 className="text-xl font-bold">General Pest Control</h3>
      </div>
      <div className="grid md:grid-cols-2">
        <div className="p-6 md:p-8 space-y-5">
          <div>
            <Label className="font-medium mb-2 block text-base">Property Type</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "house", label: "House" }, { value: "unit", label: "Unit/Townhouse" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setPropertyType(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${propertyType === opt.value ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-medium mb-2 block text-base">Bedrooms</Label>
            <div className="grid grid-cols-6 gap-2">
              {["1", "2", "3", "4", "5", "6"].map(num => (
                <button key={num} type="button" onClick={() => setBedrooms(num)}
                  className={`py-3 rounded-lg border-2 text-sm font-semibold transition-colors ${bedrooms === num ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}>
                  {num}{num === "6" && "+"}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-medium mb-2 block text-base">House Height</Label>
            <div className="grid grid-cols-2 gap-3">
              {[{ value: "single", label: "Single Storey" }, { value: "double", label: "Double Storey" }].map(opt => (
                <button key={opt.value} type="button" onClick={() => setStoreys(opt.value)}
                  className={`py-3 px-3 rounded-lg border-2 text-sm font-semibold transition-colors ${storeys === opt.value ? 'border-[#E74C3C] bg-[#E74C3C]/10 text-[#E74C3C]' : 'border-slate-200 hover:border-slate-300'}`}>
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="font-medium mb-2 block text-base">Treatment</Label>
            <div className="space-y-3">
              <button type="button" onClick={() => setTreatmentType("standard")}
                className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${treatmentType === "standard" ? 'border-[#7ABC32] bg-[#7ABC32]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-900">Standard Treatment</span>
                  {treatmentType === "standard" && <Check className="w-5 h-5 text-[#7ABC32]" />}
                </div>
                <p className="text-xs text-slate-500 mb-2">Most common for general maintenance</p>
                <ul className="space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Interior & exterior spray treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Cockroaches, spiders, ants, silverfish</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />6-month warranty</li>
                </ul>
              </button>
              <button type="button" onClick={() => setTreatmentType("comprehensive")}
                className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${treatmentType === "comprehensive" ? 'border-[#7ABC32] bg-[#7ABC32]/5' : 'border-slate-200 hover:border-slate-300'}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-semibold text-slate-900">Comprehensive Treatment</span>
                  {treatmentType === "comprehensive" && <Check className="w-5 h-5 text-[#7ABC32]" />}
                </div>
                <p className="text-xs text-slate-500 mb-2">Best for heavy infestations or full protection</p>
                <ul className="space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Everything in Standard, plus:</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Roof void dusting treatment</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Under-floor treatment (if applicable)</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />Garden perimeter spray</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#7ABC32] flex-shrink-0" />12-month warranty</li>
                </ul>
              </button>
            </div>
          </div>
          <label className="flex items-start gap-2 p-3 bg-slate-100 rounded-lg cursor-pointer">
            <Checkbox checked={rodentTreatment} onCheckedChange={setRodentTreatment} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Rodent Treatment (+${rodentPrice || 85})</span>
              {rodentTreatment && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Internal & external bait stations</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Entry point identification</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#E74C3C] flex-shrink-0" />Ongoing monitoring program</li>
                </ul>
              )}
            </div>
          </label>
          <label className="flex items-start gap-2 p-3 bg-[#D2691E]/5 border border-[#D2691E]/30 rounded-lg cursor-pointer">
            <Checkbox checked={termiteAddOn} onCheckedChange={setTermiteAddOn} className="mt-0.5" />
            <div>
              <span className="font-medium text-sm">+ Yearly Termite Inspection (+${termiteInspection || 150})</span>
              {termiteAddOn && (
                <ul className="mt-2 space-y-1 text-xs text-slate-600">
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Full property termite inspection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Thermal imaging & moisture detection</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Detailed report with photos</li>
                  <li className="flex items-center gap-1.5"><Check className="w-3 h-3 text-[#D2691E] flex-shrink-0" />Early detection saves thousands</li>
                </ul>
              )}
            </div>
          </label>
        </div>
        <div className="bg-slate-50 p-6 md:p-8 border-t md:border-t-0 md:border-l">
          {!submitted ? (
            <>
              <div className="bg-[#E74C3C] rounded-xl overflow-hidden mb-5">
                <div className="bg-[#c0392b] px-4 py-2.5 flex items-center justify-center">
                  <img src="https://customer-assets.emergentagent.com/job_1dcfe10c-2a6c-4f88-b8f3-7699155d0720/artifacts/9wru6aby_IMG_1548.png" alt="Pest Free" className="h-10" />
                </div>
                <div className="h-1" style={{ background: 'linear-gradient(90deg, #7ABC32, #5BC0DE)' }} />
                <div className="p-4 text-white text-center">
                  <div className="flex items-baseline justify-center gap-2">
                    <span className="text-lg text-white/50 line-through">${totalPrice}</span>
                    <span className="text-3xl font-bold">${discountedPrice}</span>
                    <span className="text-sm text-white/70">per visit</span>
                  </div>
                  <p className="text-amber-400 font-semibold text-sm">Save ${launchDiscount} (10% off)</p>
                </div>
              </div>
              <form onSubmit={handleSubmit} className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <Input required value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Friend's Name *" className="bg-white py-5" />
                  <Input required type="tel" value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} placeholder="Their Phone *" className="bg-white py-5" />
                </div>
                <Input required type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} placeholder="Their Email *" className="bg-white py-5" />
                <Textarea required value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} placeholder="Their Address *" rows={2} className="bg-white" />
                <Button type="submit" disabled={submitting} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 text-base font-semibold">
                  {submitting ? "Sending..." : "Send Quote to Friend"} <ChevronRight className="w-5 h-5 ml-1" />
                </Button>
              </form>
            </>
          ) : (
            <div className="text-center py-6">
              <div className="w-12 h-12 bg-[#7ABC32] rounded-full flex items-center justify-center mx-auto mb-3"><Check className="w-6 h-6 text-white" /></div>
              <h4 className="font-bold mb-2">Quote Sent!</h4>
              <p className="text-slate-600 text-sm mb-4">Your friend will receive a professional quote. You'll earn 10% when they book!</p>
              <Button onClick={reset} variant="outline" size="sm"><RotateCcw className="w-4 h-4 mr-1" /> Refer Another Friend</Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function ReferPage() {
  const [referrerName, setReferrerName] = useState("");
  const [started, setStarted] = useState(false);

  return (
    <>
      <>
        <title>Refer a Friend & Earn 10% | Pest Free Ipswich & Brisbane</title>
        <meta name="robots" content="noindex" />
      </>

      <Header />

      {!started ? (
        <section className="min-h-[70vh] flex items-center justify-center px-4 bg-gradient-to-b from-slate-900 to-slate-800">
          <div className="max-w-lg w-full text-center">
            <div className="w-20 h-20 bg-[#7ABC32]/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="text-4xl">💰</span>
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">Refer a Friend & Earn 10%</h1>
            <p className="text-white/70 mb-8 text-lg">Show your friend our calculator, send them a professional quote, and earn 10% commission when they book.</p>
            <div className="bg-white/10 backdrop-blur rounded-2xl p-6 text-left space-y-4">
              <Label className="text-white font-medium block">Your Name (for commission tracking)</Label>
              <Input value={referrerName} onChange={(e) => setReferrerName(e.target.value)} placeholder="Enter your name" className="bg-white py-5 text-lg" data-testid="referrer-name" />
              <Button onClick={() => { if (referrerName.trim()) setStarted(true); else toast.error("Please enter your name"); }} className="w-full bg-[#7ABC32] hover:bg-[#6AAD28] text-white py-6 text-base font-semibold" data-testid="start-referring-btn">
                Start Referring <ChevronRight className="w-5 h-5 ml-1" />
              </Button>
            </div>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-8 text-sm text-white/60">
              <div className="flex items-center gap-2"><Shield className="w-4 h-4 text-[#7ABC32]" /> 10% of every job</div>
              <div className="flex items-center gap-2"><Star className="w-4 h-4 text-yellow-400" /> No limit on referrals</div>
              <div className="flex items-center gap-2"><Check className="w-4 h-4 text-[#5BC0DE]" /> Paid after job completion</div>
            </div>
          </div>
        </section>
      ) : (
        <>
          <section className="bg-gradient-to-b from-slate-900 to-slate-800 text-white py-8 px-4">
            <div className="max-w-4xl mx-auto text-center">
              <p className="text-[#7ABC32] font-semibold mb-1">Referring as: {referrerName}</p>
              <h1 className="text-2xl sm:text-3xl font-bold mb-2">Show Your Friend Their Price</h1>
              <p className="text-white/70">Use the calculators below to get an instant quote, then send it directly to your friend.</p>
            </div>
          </section>

          <section className="py-10 px-4 bg-slate-100">
            <div className="max-w-5xl mx-auto space-y-10">
              <ReferralBirdCalculator referrerName={referrerName} />
              <ReferralPestCalculator referrerName={referrerName} />
            </div>
          </section>
        </>
      )}

      <footer className="bg-slate-950 py-6 px-4 text-center text-white/60 text-sm">
        <p>© 2025 Pest Free Ipswich & Brisbane. QBCC Licensed #15366887. Fully Insured.</p>
      </footer>
    </>
  );
}
